import React, { useState, useEffect, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  Inbox, CheckCircle, XCircle, Clock, User,
  Phone, MessageSquare, DollarSign, Filter,
  ChevronDown, ChevronUp, ExternalLink, RefreshCw,
  Briefcase, Wrench, AlertCircle, Users
} from "lucide-react";
import {
  getReceivedApplications,
  updateApplication,
  RequirementApplication,
  ApplicationStatus,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { formatDate } from "./MarketplaceShared";
import {
  MARKETPLACE_GRADIENT,
  RECEIVED_APPLICATION_STATUS_LABELS,
  RECEIVED_APPLICATION_STATUS_STYLES,
  RECEIVED_APPLICATION_STATUS_TABS,
} from "../../organisms/Dashboard/Marketplace/marketplaceConstants";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";
import MarketplaceLoader from "./MarketplaceLoader";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const STATUS_ICON: Record<ApplicationStatus, React.ReactNode> = {
  pending: <Clock size={13} />,
  accepted: <CheckCircle size={13} />,
  rejected: <XCircle size={13} />,
  withdrawn: <AlertCircle size={13} />,
};

const isLoggedIn = () => !!localStorage.getItem("access_token");

const MarketplaceReceivedApplicationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<RequirementApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeStatus, setActiveStatus] = useState<ApplicationStatus | "">("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [ownerNotes, setOwnerNotes] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/marketplace");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      }, 100);
    }
  }, [navigate]);

  const fetchApplications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await getReceivedApplications({
        status: activeStatus || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setApplications(res.results ?? []);
      setTotalCount(res.count ?? 0);
    } catch {
      showErrorToast("Failed to load applications.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeStatus, page]);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const handleAction = async (app: RequirementApplication, newStatus: "accepted" | "rejected") => {
    setActionLoading(app.id);
    try {
      await updateApplication(app.id, {
        status: newStatus,
        owner_notes: ownerNotes[app.id]?.trim() || undefined,
        reject_other_pending: newStatus === "accepted" ? true : undefined,
        fulfill_requirement: newStatus === "accepted" ? true : undefined,
      });
      if (newStatus === "accepted") {
        sendFirebaseEvent('Marketplace_Application_Accepted', 'Marketplace', 'application_accepted', app.id);
      } else {
        sendFirebaseEvent('Marketplace_Application_Rejected', 'Marketplace', 'application_rejected', app.id);
      }
      showSuccessToast(
        newStatus === "accepted"
          ? `✅ Application from ${app.applicant_name || "applicant"} accepted!`
          : `❌ Application rejected.`
      );
      fetchApplications(true);
      setExpandedId(null);
    } catch {
      showErrorToast("Failed to update application. Please try again.");
    } finally {
      setActionLoading(null);
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  if (loading) {
    return <MarketplaceLoader fullScreen />;
  }

  return (
    <PageLayout>
      <PageBody>
          {/* Page Header */}
          <PageTopRow>
            <PageTitleGroup>
              <div>
                <PageTitle>Received Applications</PageTitle>
                <PageSubtitle>
                  Manage applications on your posted equipment & service requirements
                </PageSubtitle>
              </div>
            </PageTitleGroup>
          </PageTopRow>

          {/* Status filter tabs */}
          <TabRow>
            <FilterIcon><Filter size={14} /></FilterIcon>
            {RECEIVED_APPLICATION_STATUS_TABS.map(tab => (
              <StatusTab
                key={tab.value}
                $active={activeStatus === tab.value}
                onClick={() => { setActiveStatus(tab.value as any); setPage(1); }}
              >
                {tab.label}
                {tab.value === "" && totalCount > 0 && (
                  <CountBadge>{totalCount}</CountBadge>
                )}
              </StatusTab>
            ))}
          </TabRow>

          {/* Content */}
          {applications.length === 0 ? (
            <EmptyState>
              <EmptyIcon><Inbox size={48} strokeWidth={1.2} /></EmptyIcon>
              <EmptyTitle>No applications yet</EmptyTitle>
              <EmptyDesc>
                {activeStatus
                  ? `No ${activeStatus} applications found.`
                  : "When people apply to your requirements, they'll appear here."}
              </EmptyDesc>
              <EmptyAction onClick={() => navigate("/marketplace/post-requirement")}>
                Post a Requirement
              </EmptyAction>
            </EmptyState>
          ) : (
            <>
              <ResultCount>
                Showing {applications.length} of {totalCount} application{totalCount !== 1 ? "s" : ""}
              </ResultCount>
              <AppList>
                {applications.map(app => {
                  const statusStyle = RECEIVED_APPLICATION_STATUS_STYLES[app.status];
                  const isExpanded = expandedId === app.id;
                  const isPending = app.status === "pending";

                  return (
                    <AppCard key={app.id} $status={app.status}>
                      {/* Card Header */}
                      <CardHeader onClick={() => setExpandedId(isExpanded ? null : app.id)}>
                        <CardLeft>
                          <ApplicantAvatar>
                            <User size={18} />
                          </ApplicantAvatar>
                          <CardInfo>
                            <ApplicantName>{app.applicant_name || `Applicant #${app.applicant}`}</ApplicantName>
                            <ReqTitle>
                              <ReqTypePill $type={app.requirement_type}>
                                {app.requirement_type === "equipment_requirement" ? (
                                  <><Wrench size={10} /> Equipment</>
                                ) : app.requirement_type === "bulk_custom_unit" ? (
                                  <><Users size={10} /> Custom Unit / Bulk</>
                                ) : (
                                  <><Briefcase size={10} /> Service</>
                                )}
                              </ReqTypePill>
                              {app.requirement_title || "Requirement"}
                            </ReqTitle>
                          </CardInfo>
                        </CardLeft>

                        <CardRight>
                          {app.proposed_rate && (
                            <RateChip>
                              ₹{app.proposed_rate}
                            </RateChip>
                          )}
                          <StatusBadge $color={statusStyle.color} $bg={statusStyle.bg}>
                            {STATUS_ICON[app.status]} {RECEIVED_APPLICATION_STATUS_LABELS[app.status]}
                          </StatusBadge>
                          <ExpandBtn $expanded={isExpanded}>
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </ExpandBtn>
                        </CardRight>
                      </CardHeader>

                      {/* Expanded Detail */}
                      {isExpanded && (
                        <CardExpanded>
                          <Divider />
                          <DetailGrid>
                            {app.applicant_phone && (
                              <DetailItem>
                                <DetailIcon><Phone size={14} /></DetailIcon>
                                <DetailLabel>Phone</DetailLabel>
                                <DetailValue>
                                  <a href={`tel:${app.applicant_phone}`}>{app.applicant_phone}</a>
                                </DetailValue>
                              </DetailItem>
                            )}
                            <DetailItem>
                              <DetailIcon><Clock size={14} /></DetailIcon>
                              <DetailLabel>Applied On</DetailLabel>
                              <DetailValue>
                                {formatDate(app.created_at)}
                              </DetailValue>
                            </DetailItem>
                            <DetailItem>
                              <DetailIcon><ExternalLink size={14} /></DetailIcon>
                              <DetailLabel>Requirement Status</DetailLabel>
                              <DetailValue>{app.requirement_status || "—"}</DetailValue>
                            </DetailItem>
                          </DetailGrid>

                          {app.message && (
                            <MessageBox>
                              <MessageLabel>
                                <MessageSquare size={13} /> Message from applicant
                              </MessageLabel>
                              <MessageText>{app.message}</MessageText>
                            </MessageBox>
                          )}

                          {app.owner_notes && (
                            <MessageBox $variant="note">
                              <MessageLabel>Your Notes</MessageLabel>
                              <MessageText>{app.owner_notes}</MessageText>
                            </MessageBox>
                          )}

                          {isPending && (
                            <ActionArea>
                              <NotesInput
                                placeholder="Add a note (optional, sent to applicant)…"
                                value={ownerNotes[app.id] || ""}
                                onChange={e => setOwnerNotes(prev => ({ ...prev, [app.id]: e.target.value }))}
                                rows={2}
                              />
                              <ActionBtns>
                                <RejectBtn
                                  onClick={() => handleAction(app, "rejected")}
                                  disabled={actionLoading === app.id}
                                >
                                  <XCircle size={15} />
                                  {actionLoading === app.id ? "Updating…" : "Reject"}
                                </RejectBtn>
                                <AcceptBtn
                                  onClick={() => handleAction(app, "accepted")}
                                  disabled={actionLoading === app.id}
                                >
                                  <CheckCircle size={15} />
                                  {actionLoading === app.id ? "Updating…" : "Accept"}
                                </AcceptBtn>
                              </ActionBtns>
                            </ActionArea>
                          )}
                        </CardExpanded>
                      )}
                    </AppCard>
                  );
                })}
              </AppList>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination>
                  <PageBtn disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</PageBtn>
                  <PageInfo>Page {page} of {totalPages}</PageInfo>
                  <PageBtn disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next →</PageBtn>
                </Pagination>
              )}
            </>
          )}
        </PageBody>
    </PageLayout>
  );
};

export default MarketplaceReceivedApplicationsPage;



const PageLayout = styled.div`
  min-height: calc(100vh - 64px);
  width: 100%;
  background: var(--mp-bodyBackground, #f7f8fc);
`;

const PageBody = styled.div`
  max-width: 1680px;
  margin: 0 auto;
  padding: 32px 40px 80px;
  width: 100%;
  box-sizing: border-box;
  animation: ${fadeIn} 0.3s ease;
`;

const PageTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const PageTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const PageIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${MARKETPLACE_GRADIENT};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(201,77,170,0.25);
  flex-shrink: 0;
`;

const PageTitle = styled.h1`
  margin: 0 0 4px;
  font-size: 28px;
  font-weight: 800;
  color: var(--mp-textColor, #171717);
  font-family: Urbanist, sans-serif;
`;

const PageSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: var(--mp-subColor, rgba(23,23,23,0.55));
  font-family: Montserrat, sans-serif;
`;

const RefreshBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23,23,23,0.12));
  border-radius: 10px;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  color: var(--mp-textColor, #171717);
  transition: all 0.2s;
  &:hover:not(:disabled) { background: var(--mp-bodyBackground, #f7f8fc); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SpinIcon = styled.span<{ $spinning: boolean }>`
  display: flex;
  ${p => p.$spinning && css`animation: ${spin} 0.8s linear infinite;`}
`;

const TabRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const FilterIcon = styled.div`
  color: var(--mp-subColor, rgba(23,23,23,0.4));
  margin-right: 4px;
  display: flex;
  align-items: center;
`;

const StatusTab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border-radius: 100px;
  border: 1.5px solid ${p => p.$active ? "transparent" : "var(--mp-border, rgba(23,23,23,0.1))"};
  background: ${p => p.$active ? "var(--mp-iconGrad, linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%))" : "var(--mp-cardBackground, white)"};
  color: ${p => p.$active ? "white" : "var(--mp-subColor, rgba(23,23,23,0.7))"};
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #c94daa; }
`;

const CountBadge = styled.span`
  background: rgba(255,255,255,0.25);
  border-radius: 100px;
  padding: 1px 7px;
  font-size: 11px;
`;

const ResultCount = styled.p`
  font-size: 12px;
  color: var(--mp-subColor, rgba(23,23,23,0.45));
  font-family: Montserrat, sans-serif;
  margin: 0 0 16px;
`;

const AppList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const shimmer = keyframes`
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

const SkeletonList = styled.div`display: flex; flex-direction: column; gap: 12px;`;
const SkeletonCard = styled.div`
  height: 80px;
  border-radius: 14px;
  background: linear-gradient(90deg, var(--mp-bodyBackground, #f0f0f0) 25%, var(--mp-cardBackground, #e8e8e8) 50%, var(--mp-bodyBackground, #f0f0f0) 75%);
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s infinite;
`;

const AppCard = styled.div<{ $status: ApplicationStatus }>`
  background: var(--mp-cardBackground, white);
  border: 1.5px solid ${p => RECEIVED_APPLICATION_STATUS_STYLES[p.$status].border};
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.1); }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  gap: 12px;
  user-select: none;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 14px;
    gap: 10px;
  }
`;

const CardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const ApplicantAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255,201,81,0.15), rgba(201,77,170,0.15));
  border: 1.5px solid rgba(201,77,170,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c94daa;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ApplicantName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #171717;
  font-family: Urbanist, sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ReqTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(23,23,23,0.55);
  font-family: Montserrat, sans-serif;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ReqTypePill = styled.span<{ $type?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 100px;
  font-size: 10px;
  font-weight: 700;
  background: ${p => p.$type === "equipment_requirement"
    ? "rgba(124,77,255,0.10)" : "rgba(201,77,170,0.10)"};
  color: ${p => p.$type === "equipment_requirement" ? "#7c4dff" : "#c94daa"};
  flex-shrink: 0;
`;

const CardRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  @media (max-width: 600px) {
    width: 100%;
    justify-content: space-between;
    margin-top: 6px;
  }
`;

const RateChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 10px;
  background: rgba(16,185,129,0.08);
  color: #10b981;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  font-family: Montserrat, sans-serif;
`;

const StatusBadge = styled.div<{ $color: string; $bg: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 8px;
  background: ${p => p.$bg};
  color: ${p => p.$color};
  font-size: 12px;
  font-weight: 700;
  font-family: Montserrat, sans-serif;
  white-space: nowrap;
`;

const ExpandBtn = styled.div<{ $expanded: boolean }>`
  color: rgba(23,23,23,0.4);
  display: flex;
  align-items: center;
  transition: transform 0.2s;
  transform: ${p => p.$expanded ? "rotate(0)" : "rotate(0)"};
`;

const CardExpanded = styled.div`
  padding: 0 20px 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(23,23,23,0.06);
  margin-bottom: 16px;
`;

const DetailGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--mp-bodyBackground, #f7f8fc);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  padding: 8px 12px;
  border-radius: 8px;

  @media (max-width: 480px) {
    justify-content: space-between;
  }
`;

const DetailIcon = styled.div`color: var(--mp-subColor, rgba(23,23,23,0.4)); display: flex; align-items: center;`;
const DetailLabel = styled.span`font-size: 11px; font-weight: 700; color: var(--mp-subColor, rgba(23,23,23,0.45)); font-family: Montserrat, sans-serif; text-transform: uppercase; letter-spacing: 0.04em;`;
const DetailValue = styled.span`
  font-size: 13px; font-weight: 600; color: var(--mp-textColor, #171717); font-family: Urbanist, sans-serif;
  a { color: #c94daa; text-decoration: none; }
  a:hover { text-decoration: underline; }
`;

const MessageBox = styled.div<{ $variant?: "note" }>`
  background: ${p => p.$variant === "note" ? "rgba(124,77,255,0.05)" : "rgba(23,23,23,0.03)"};
  border: 1px solid ${p => p.$variant === "note" ? "rgba(124,77,255,0.15)" : "var(--mp-border, rgba(23,23,23,0.07))"};
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
`;

const MessageLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  color: var(--mp-subColor, rgba(23,23,23,0.5));
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-family: Montserrat, sans-serif;
  margin-bottom: 6px;
`;

const MessageText = styled.p`
  margin: 0;
  font-size: 13px;
  color: var(--mp-textColor, #171717);
  font-family: Urbanist, sans-serif;
  line-height: 1.6;
`;

const ActionArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
`;

const NotesInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1px solid var(--mp-border, rgba(23,23,23,0.12));
  border-radius: 10px;
  font-size: 13px;
  font-family: Urbanist, sans-serif;
  color: var(--mp-textColor, #171717);
  background: var(--mp-cardBackground, white);
  resize: none;
  outline: none;
  &:focus { border-color: #c94daa66; box-shadow: 0 0 0 3px rgba(201,77,170,0.06); }
  &::placeholder { color: var(--mp-subColor, rgba(23,23,23,0.35)); }
`;

const ActionBtns = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const RejectBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  background: var(--mp-cardBackground, white);
  border: 1.5px solid rgba(239,68,68,0.4);
  border-radius: 9px;
  color: #ef4444;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: all 0.2s;
  &:hover:not(:disabled) { background: rgba(239,68,68,0.06); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const AcceptBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 24px;
  background: var(--mp-iconGrad, linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%));
  border: none;
  border-radius: 9px;
  color: white;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(201,77,170,0.25);
  transition: opacity 0.2s;
  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  color: rgba(201,77,170,0.3);
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 800;
  color: var(--mp-textColor, #171717);
  font-family: Urbanist, sans-serif;
`;

const EmptyDesc = styled.p`
  margin: 0 0 24px;
  font-size: 14px;
  color: var(--mp-subColor, rgba(23,23,23,0.5));
  font-family: Montserrat, sans-serif;
  max-width: 340px;
`;

const EmptyAction = styled.button`
  padding: 10px 28px;
  background: var(--mp-iconGrad, linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%));
  color: white;
  border: none;
  border-radius: 10px;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(201,77,170,0.25);
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 32px;
`;

const PageBtn = styled.button`
  padding: 8px 20px;
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23,23,23,0.12));
  border-radius: 9px;
  font-family: Urbanist, sans-serif;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover:not(:disabled) { background: var(--mp-bodyBackground, #f7f8fc); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PageInfo = styled.span`
  font-size: 13px;
  font-family: Montserrat, sans-serif;
  color: var(--mp-subColor, rgba(23,23,23,0.6));
`;
